/**
 * Editing a cut list.
 *
 * The editor draws **removals** and stores what is **kept** — the two are inverses, and every
 * operation here is stated in whichever of the two makes it obvious. Cutting divides the kept
 * range the playhead is in and removes nothing; the two halves meet at a **seam**, and pulling
 * its edges apart is what opens a removal. Putting a removal back is a merge of the two ranges
 * around it, and trimming an end is the same gesture with the source's own edge on one side.
 *
 * Pure, and separate from the component, because this is the part with rules: ranges stay ordered
 * and disjoint, nothing shrinks below what Mux will clip, and no operation may produce a list the
 * server would refuse.
 */

/** One stretch the cut keeps, in the source's timeline. */
export interface KeptRange {
	startTime: number;
	endTime: number;
}

/** The shortest stretch that may be kept, matching the server's rule and Mux's own minimum. */
export const MINIMUM_KEPT_SECONDS = 0.5;

/**
 * What the editor actually holds a range to: a millisecond above the rule.
 *
 * The two sides measure the same distance differently. The editor builds an edge by adding the
 * minimum to the other one; the server subtracts one edge from the other. Those disagree in the
 * last bit of a float — `15.9 + 0.5` gives a number whose difference from `15.9` is 0.4999999999999982
 * — so a range the editor believed was exactly the minimum came back refused as too short. The
 * margin is far below a frame and puts the whole class of disagreement out of reach.
 */
const EDITOR_MINIMUM_SECONDS = MINIMUM_KEPT_SECONDS + 0.001;

/** One stretch the cut removes: what the timeline hatches in red. */
export interface Removal {
	startTime: number;
	endTime: number;
	/**
	 * Which kept ranges it sits between. `-1` means it runs from the start of the source to the
	 * first kept range, and `segments.length - 1` that it runs from the last one to the end.
	 */
	after: number;
}

/**
 * The removals a kept list implies: the gaps before, between, and after its ranges.
 *
 * Derived rather than stored, because storing both is storing the same fact twice and inviting
 * them to disagree.
 *
 * @param segments - The kept ranges, ordered and disjoint.
 * @param duration - The source's length.
 * @returns Every gap wide enough to see, in order.
 */
export function removalsOf(segments: KeptRange[], duration: number): Removal[] {
	const removals: Removal[] = [];
	const first = segments.at(0);
	if (first && first.startTime > 0) {
		removals.push({ startTime: 0, endTime: first.startTime, after: -1 });
	}
	segments.forEach((segment, index) => {
		const next = segments[index + 1];
		if (next && next.startTime > segment.endTime) {
			removals.push({ startTime: segment.endTime, endTime: next.startTime, after: index });
		}
	});
	const last = segments.at(-1);
	if (last && last.endTime < duration) {
		removals.push({ startTime: last.endTime, endTime: duration, after: segments.length - 1 });
	}
	return removals;
}

/** How much video the cut leaves. */
export function keptDuration(segments: KeptRange[]) {
	return segments.reduce((total, segment) => total + (segment.endTime - segment.startTime), 0);
}

/** Which kept range a moment falls in, or -1 when it falls in a removal. */
export function segmentAt(segments: KeptRange[], time: number) {
	return segments.findIndex((segment) => time >= segment.startTime && time <= segment.endTime);
}

/**
 * Cut the kept range the playhead is in, in two.
 *
 * Nothing is removed. The two halves meet at the playhead, and each brings an edge of its own to
 * that seam — pulling one of them away is what opens a removal, and how far is the author's to
 * decide by looking at the frame. The scissors that took a fixed stretch out instead had to guess
 * how much, and every guess was wrong somewhere.
 *
 * @param segments - The kept ranges.
 * @param time - Where the playhead stands.
 * @returns The new list, or null when a half would be born shorter than the shortest range the
 * server accepts — in which case the scissors is offered as disabled.
 */
export function splitAt(segments: KeptRange[], time: number): KeptRange[] | null {
	const index = segmentAt(segments, time);
	if (index === -1) return null;
	const segment = segments.at(index);
	if (!segment) return null;
	// Both halves have to stand on their own from the moment they exist, because either of them
	// can be the one the author never touches again.
	if (time - segment.startTime < EDITOR_MINIMUM_SECONDS) return null;
	if (segment.endTime - time < EDITOR_MINIMUM_SECONDS) return null;

	return [
		...segments.slice(0, index),
		{ startTime: segment.startTime, endTime: time },
		{ startTime: time, endTime: segment.endTime },
		...segments.slice(index + 1),
	];
}

/**
 * Whether a kept range meets the next one with no removal between them: the seam a cut leaves.
 *
 * It is what the timeline draws two handles against instead of one, since at a seam the two edges
 * stand at the same place and the one on top would otherwise be the only one that can be grabbed.
 *
 * @param segments - The kept ranges.
 * @param index - The range whose far edge is in question.
 * @returns True when the next range starts exactly where this one ends.
 */
export function isSeam(segments: KeptRange[], index: number) {
	const segment = segments.at(index);
	const next = segments.at(index + 1);
	// Exact equality rather than a tolerance: both producers write the identical number — the cut
	// writes one moment into two ranges, and a drag towards a neighbour stops on the neighbour's
	// own value — so anything that is not equal is a gap somebody made on purpose.
	return Boolean(segment && next && next.startTime === segment.endTime);
}

/**
 * Close every seam, leaving one range per stretch that actually plays.
 *
 * A cut nobody pulled apart removes nothing, and the list it leaves describes the same video as
 * the list before it. Submitting that would render a video identical to the one already playing,
 * and comparing it against what is published would read as a change. Both are answered by folding
 * touching ranges back together before either happens.
 *
 * @param segments - The kept ranges.
 * @returns The same stretches, with no two of them meeting.
 */
export function mergeTouching(segments: KeptRange[]): KeptRange[] {
	return segments.reduce<KeptRange[]>((merged, segment) => {
		const previous = merged.at(-1);
		if (previous && previous.endTime === segment.startTime) {
			merged[merged.length - 1] = { startTime: previous.startTime, endTime: segment.endTime };
			return merged;
		}
		merged.push({ ...segment });
		return merged;
	}, []);
}

/**
 * Put a removal back.
 *
 * At the ends that means growing the outermost range to the edge of the source; in the middle it
 * means merging the two ranges the removal sits between, which is the same operation seen from
 * the other side.
 *
 * @param segments - The kept ranges.
 * @param removal - The gap to close.
 * @param duration - The source's length.
 * @returns The new list.
 */
export function restoreRemoval(
	segments: KeptRange[],
	removal: Removal,
	duration: number,
): KeptRange[] {
	if (removal.after === -1) {
		const [first, ...rest] = segments;
		return first ? [{ startTime: 0, endTime: first.endTime }, ...rest] : segments;
	}
	if (removal.after === segments.length - 1) {
		const last = segments.at(-1);
		return last
			? [...segments.slice(0, -1), { startTime: last.startTime, endTime: duration }]
			: segments;
	}
	const left = segments.at(removal.after);
	const right = segments.at(removal.after + 1);
	if (!left || !right) return segments;
	return [
		...segments.slice(0, removal.after),
		{ startTime: left.startTime, endTime: right.endTime },
		...segments.slice(removal.after + 2),
	];
}

/**
 * Move one edge of one kept range, without letting it pass its neighbours.
 *
 * The bounds are what keep the list valid whatever the pointer does: a dragged edge stops at the
 * far edge of its own range less the minimum, and at the near edge of the range beyond the
 * removal — so ranges never overlap and never vanish, and the server never has to refuse a list
 * the editor produced.
 *
 * @param segments - The kept ranges.
 * @param index - Which range is being dragged.
 * @param edge - Which of its two edges.
 * @param time - Where the pointer is.
 * @param duration - The source's length.
 * @returns The new list.
 */
export function moveBoundary(
	segments: KeptRange[],
	index: number,
	edge: "start" | "end",
	time: number,
	duration: number,
): KeptRange[] {
	const segment = segments[index];
	if (!segment) return segments;

	const floor = segments[index - 1]?.endTime ?? 0;
	const ceiling = segments[index + 1]?.startTime ?? duration;
	const moved =
		edge === "start"
			? {
					startTime: clamp(time, floor, segment.endTime - EDITOR_MINIMUM_SECONDS),
					endTime: segment.endTime,
				}
			: {
					startTime: segment.startTime,
					endTime: clamp(time, segment.startTime + EDITOR_MINIMUM_SECONDS, ceiling),
				};

	// The last word, measured the way the server measures it. The bounds above are built by adding
	// to and subtracting from the neighbours, and a window that has closed — the two bounds
	// crossing by a fraction of a bit — would otherwise be answered with an edge that has slid over
	// its neighbour. An edge that cannot move without breaking a rule does not move; the list in
	// hand already keeps them.
	if (moved.endTime - moved.startTime < MINIMUM_KEPT_SECONDS) return segments;
	if (moved.startTime < floor || moved.endTime > ceiling) return segments;

	return segments.map((current, at) => (at === index ? moved : current));
}

/**
 * The nearest moment inside a kept range.
 *
 * The playhead does not enter a removal, so a position that falls in one lands on whichever of its
 * edges is nearer — which both refuses the position and shows where the edge is.
 *
 * @param segments - The kept ranges.
 * @param time - The moment asked for.
 * @returns The moment to use instead, or the same one when it was already kept.
 */
export function clampToKept(segments: KeptRange[], time: number) {
	if (segments.length === 0) return time;
	if (segmentAt(segments, time) !== -1) return time;

	const edges = segments.flatMap((segment) => [segment.startTime, segment.endTime]);
	return edges.reduce((best, edge) =>
		Math.abs(edge - time) < Math.abs(best - time) ? edge : best,
	);
}

/** What playback should do at a moment: carry on, jump over a removal, or stop at the cut's end. */
export type Carry =
	| { kind: "play" }
	| { kind: "jump"; time: number }
	| { kind: "stop"; time: number };

/**
 * What playback should do on arriving at a moment.
 *
 * The editor previews the video the cut produces, so a removal is skipped rather than played, and
 * past the last kept range there is nowhere to go.
 *
 * The tolerance is what keeps a jump from repeating itself. A player seeks to the frame at or
 * below the moment asked for, so arriving at the start of a kept range reads as still standing in
 * the removal before it — and the jump would then be asked for again by the very events the seek
 * fires, for ever. Within the tolerance the moment counts as arrived; the few milliseconds it
 * plays are the last of a stretch being cut anyway.
 *
 * @param segments - The kept ranges.
 * @param time - Where playback has arrived.
 * @param tolerance - How far short of a range's start still counts as being in it.
 * @returns What to do, and where.
 */
export function carryFrom(segments: KeptRange[], time: number, tolerance: number): Carry {
	if (segments.length === 0 || segmentAt(segments, time) !== -1) return { kind: "play" };

	const next = nextKeptTime(segments, time);
	if (next !== null) {
		return next - time <= tolerance ? { kind: "play" } : { kind: "jump", time: next };
	}

	const end = segments.at(-1)?.endTime;
	return end === undefined ? { kind: "play" } : { kind: "stop", time: end };
}

/**
 * Where playback goes on reaching a moment, so a removal is skipped rather than played.
 *
 * @param segments - The kept ranges.
 * @param time - Where playback has arrived.
 * @returns The start of the next kept range, or null when the cut is over.
 */
export function nextKeptTime(segments: KeptRange[], time: number) {
	const next = segments.find((segment) => segment.startTime > time);
	return next ? next.startTime : null;
}

function clamp(value: number, low: number, high: number) {
	return Math.min(Math.max(value, low), high);
}
